import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import FilterSelect from "./FilterSelect";
import DateRangeFilter from "./DateRangeFilter";
import { ClearIcon, HashIcon, SortIcon, StatusIcon } from "./icons";
import {
  DEFAULT_SORT,
  GET_SORT_OPTIONS,
  GET_STATUS_OPTIONS,
  IContractsFilters,
  hasNarrowingFilters,
} from "./constants";
import styles from "./styles.module.scss";

interface Props {
  tabType: string;
  filters: IContractsFilters;
  onChange: (patch: Partial<IContractsFilters>) => void;
  onReset: () => void;
}

const ContractsFilters: React.FC<Props> = ({
  tabType,
  filters,
  onChange,
  onReset,
}) => {
  const { t } = useTranslation();
  const statusOptions = GET_STATUS_OPTIONS(t, tabType);

  const hasFilters =
    hasNarrowingFilters(filters) || filters.sort !== DEFAULT_SORT;

  return (
    <div className={styles.bar}>
      <div className={styles.group}>
        {statusOptions.length > 0 && (
          <FilterSelect
            icon={<StatusIcon />}
            placeholder={t("lbl_contracts_filter_all_statuses")}
            options={statusOptions}
            value={filters.status}
            onChange={(status) => onChange({ status })}
            clearable
          />
        )}

        <DateRangeFilter
          value={filters.dateRange}
          onChange={(dateRange) => onChange({ dateRange })}
        />

        <div
          className={classNames(styles.control, styles.inputControl, {
            [styles.controlActive]: !!filters.referenceId,
          })}>
          <span className={styles.controlIcon}>
            <HashIcon />
          </span>
          <input
            dir="auto"
            className={styles.textInput}
            value={filters.referenceId}
            placeholder={t("lbl_contracts_filter_reference_id") as string}
            aria-label={t("lbl_contracts_filter_reference_id") as string}
            onChange={(event) => onChange({ referenceId: event.target.value })}
          />
          {!!filters.referenceId && (
            <button
              type="button"
              className={styles.clearButton}
              aria-label={t("lbl_org_act_clear") as string}
              onClick={() => onChange({ referenceId: "" })}>
              <ClearIcon />
            </button>
          )}
        </div>

        {hasFilters && (
          <button type="button" className={styles.resetLink} onClick={onReset}>
            {t("lbl_contracts_filter_reset")}
          </button>
        )}
      </div>

      <div className={styles.sortGroup}>
        <span className={styles.sortLabel}>{t("lbl_contracts_sort_by")}</span>
        <FilterSelect
          icon={<SortIcon />}
          placeholder={t("lbl_contracts_sort_by")}
          options={GET_SORT_OPTIONS(t)}
          value={filters.sort}
          neutralValue={DEFAULT_SORT}
          onChange={(sort) => onChange({ sort: sort || DEFAULT_SORT })}
          alignEnd
        />
      </div>
    </div>
  );
};

export default ContractsFilters;
